import { Link } from "react-router-dom";
import { AlertCircle, Clock } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

export function SubscriptionStatusBanner() {
  const { subscription, loading } = useSubscription();
  if (loading || !subscription) return null;

  const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
  const daysLeft = periodEnd ? Math.ceil((periodEnd.getTime() - Date.now()) / 86_400_000) : null;

  if (subscription.status === "past_due") {
    return (
      <Banner tone="danger" icon={<AlertCircle className="h-4 w-4" />}>
        Your last payment failed. Update your payment method to keep access.{" "}
        <Link to="/pricing" className="underline font-medium">Manage billing</Link>
      </Banner>
    );
  }
  if (subscription.status === "trialing" && daysLeft !== null && daysLeft <= 2) {
    return (
      <Banner tone="warning" icon={<Clock className="h-4 w-4" />}>
        Your free trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}.{" "}
        <Link to="/pricing" className="underline font-medium">Manage plan</Link>
      </Banner>
    );
  }
  if (subscription.cancel_at_period_end && daysLeft !== null && daysLeft >= 0) {
    return (
      <Banner tone="warning" icon={<Clock className="h-4 w-4" />}>
        Your subscription ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}.{" "}
        <Link to="/pricing" className="underline font-medium">Resume</Link>
      </Banner>
    );
  }
  return null;
}

function Banner({ tone, icon, children }: { tone: "warning" | "danger"; icon: React.ReactNode; children: React.ReactNode }) {
  const cls = tone === "danger"
    ? "bg-red-100 border-red-300 text-red-900"
    : "bg-amber-100 border-amber-300 text-amber-900";
  return (
    <div className={`w-full border-b px-4 py-2 text-center text-sm flex items-center justify-center gap-2 ${cls}`}>
      {icon}
      <span>{children}</span>
    </div>
  );
}
